import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const COUNTRIES = [
  { dial: '91',  flag: '🇮🇳', name: 'India',        digits: 10 },
  { dial: '1',   flag: '🇺🇸', name: 'USA / Canada', digits: 10 },
  { dial: '44',  flag: '🇬🇧', name: 'UK',           digits: 10 },
  { dial: '41',  flag: '🇨🇭', name: 'Switzerland',  digits:  9 },
  { dial: '971', flag: '🇦🇪', name: 'UAE',          digits:  9 },
  { dial: '65',  flag: '🇸🇬', name: 'Singapore',    digits:  8 },
  { dial: '61',  flag: '🇦🇺', name: 'Australia',    digits:  9 },
  { dial: '27',  flag: '🇿🇦', name: 'South Africa', digits:  9 },
  { dial: '60',  flag: '🇲🇾', name: 'Malaysia',     digits:  9 },
];

/**
 * Parse a combined string like "919876543210" into { country, localNumber }.
 * Tries longest dial codes first to avoid ambiguity.
 * Only strips dial code if remaining digits match the expected local length.
 */
function parseValue(raw = '') {
  const digits = raw.replace(/\D/g, '');
  // Try longest dial codes first
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (digits.startsWith(c.dial)) {
      const local = digits.slice(c.dial.length);
      // Only accept if local part has the exact expected digit count
      if (local.length === c.digits) {
        return { country: c, localNumber: local };
      }
    }
  }
  // Default: India, treat entire string as local number (legacy 10-digit)
  return { country: COUNTRIES[0], localNumber: digits };
}

/**
 * PhoneInput — country flag + dial code + number input.
 *
 * Props:
 *   value      – combined digits string, e.g. "919876543210"
 *   onChange   – (value: string) => void  (same format)
 *   disabled   – bool
 *   placeholder– string
 *   inputProps – extra props forwarded to <input>
 *   className  – wrapper class
 */
export function PhoneInput({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Phone number',
  inputProps = {},
  className = '',
}) {
  const { country: initCountry, localNumber: initLocal } = parseValue(value);
  const [country, setCountry] = useState(initCountry);
  const [localNumber, setLocalNumber] = useState(initLocal);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  // Tracks the last combined value WE emitted, so we can ignore the parent
  // echoing it back — including when the parent re-normalizes it and adds an
  // extra dial-code prefix (e.g. "91" + "91XXXXXXXX"). This prevents the
  // feedback loop that corrupted numbers mid-typing.
  const lastEmittedRef = useRef(initCountry.dial + initLocal);

  // Sync when value changes from OUTSIDE (e.g. form reset, pre-fill from DB).
  // Ignore any value that is just our own last emit (optionally re-prefixed).
  useEffect(() => {
    const incoming = (value || '').replace(/\D/g, '');
    const mine = lastEmittedRef.current;
    if (incoming === mine || incoming === country.dial + mine) return;
    const { country: c, localNumber: l } = parseValue(value);
    setCountry(c);
    setLocalNumber(l);
    lastEmittedRef.current = c.dial + l;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const emit = (dial, num) => {
    lastEmittedRef.current = dial + num;
    if (onChange) onChange(dial + num);
  };

  const handleCountrySelect = (c) => {
    setCountry(c);
    setOpen(false);
    emit(c.dial, localNumber);
  };

  const handleNumberChange = (e) => {
    let num = e.target.value.replace(/\D/g, '');
    // Auto-strip country code if user accidentally pastes/types the full
    // international number (e.g. pastes "917537899999" into India field).
    // Only strip when the part after the dial code has exactly the expected
    // local digit count — avoids stripping a legitimate local prefix.
    if (num.startsWith(country.dial)) {
      const withoutCode = num.slice(country.dial.length);
      if (withoutCode.length === country.digits) {
        num = withoutCode;
      }
    }
    setLocalNumber(num);
    emit(country.dial, num);
  };

  return (
    <div className={`relative flex items-stretch rounded-md border border-input shadow-sm ${disabled ? 'opacity-50' : ''} ${className}`} ref={dropdownRef}>
      {/* Country selector button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className="flex items-center gap-1 px-2.5 border-r border-input bg-muted/40 rounded-l-md hover:bg-muted transition-colors select-none min-w-[72px] justify-between"
        tabIndex={-1}
        data-testid="phone-country-selector"
      >
        <span className="text-base leading-none">{country.flag}</span>
        <span className="text-xs font-medium text-muted-foreground">+{country.dial}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
      </button>

      {/* Number input */}
      <input
        type="tel"
        inputMode="numeric"
        value={localNumber}
        onChange={handleNumberChange}
        disabled={disabled}
        placeholder={placeholder}
        className="flex-1 bg-transparent px-3 py-2 text-sm rounded-r-md focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground min-w-0"
        {...inputProps}
      />

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-10 left-0 bg-popover border border-border rounded-md shadow-lg overflow-hidden min-w-[220px]" style={{ top: 'auto' }}>
          {COUNTRIES.map(c => (
            <button
              key={c.dial}
              type="button"
              onClick={() => handleCountrySelect(c)}
              className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent text-left transition-colors ${c.dial === country.dial ? 'bg-accent font-medium' : ''}`}
            >
              <span className="text-base">{c.flag}</span>
              <span className="text-muted-foreground">+{c.dial}</span>
              <span>{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Normalize a legacy phone (10-digit Indian) to prefixed format for PhoneInput.
 * E.g. "9876543210" → "919876543210"
 *      "919876543210" → "919876543210" (already prefixed)
 */
export function normalizePhone(raw = '') {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // If exactly 10 digits starting with Indian mobile prefix → add 91
  if (digits.length === 10 && /^[6-9]/.test(digits)) return '91' + digits;
  return digits;
}

/**
 * Format for display / links: returns "+919876543210"
 */
export function formatPhoneE164(stored = '') {
  if (!stored) return '';
  const digits = stored.replace(/\D/g, '');
  return '+' + digits;
}
